import { createFileRoute, Link } from '@tanstack/react-router';
import { Activity, AlertTriangle, CheckCircle2, Radio, ScrollText } from 'lucide-react';

import { TriageRow, type TriageItem } from '@/components/data/TriageRow';
import { PageHeader } from '@/components/layout/PageHeader';
import { RequireCapability } from '@/components/layout/RequireCapability';
import { LiveDataIndicator } from '@/components/primitives/LiveDataIndicator';
import { StateChip } from '@/components/primitives/StateChip';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { Skeleton } from '@/components/states/Skeleton';
import { StateBlock } from '@/components/states/StateBlock';
import {
  formatAge,
  useDashboard,
  type AttentionItem,
  type DashboardPayload,
} from '@/features/dashboard/dashboard';
import { compareQueues, QUEUE_STATUS_STYLE } from '@/features/dataops/queue-health';

export const Route = createFileRoute('/')({
  component: DashboardScreen,
});

/**
 * Ops home. Answers "what needs me right now?", not vanity metrics — revenue and
 * growth live behind `metrics.view` on their own screen for a different audience.
 *
 * The attention queue is the entire first screen on mobile, with everything else
 * below the fold, so the layout puts it first and unconditionally.
 */
function DashboardScreen() {
  const { data, isPending, isFetching, isError, error, refetch, dataUpdatedAt } =
    useDashboard();

  return (
    <RequireCapability capability="dashboard.view">
      <div className="flex flex-col gap-4">
        <PageHeader
          eyebrow="Operations home"
          title="What needs me right now"
          meta={data ? <AttentionSummary counts={data.counts} /> : undefined}
          actions={
            <LiveDataIndicator
              updatedAt={dataUpdatedAt || undefined}
              isFetching={isFetching}
              isLive
              staleAfterMs={90_000}
              onRefresh={() => void refetch()}
            />
          }
        />

        {isError ? (
          <ErrorState error={error} subject="the dashboard" onRetry={() => void refetch()} />
        ) : isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton height={72} />
            <Skeleton height={72} />
            <Skeleton height={72} />
          </div>
        ) : (
          <>
            <AttentionQueue data={data} />

            <div className="grid gap-4 lg:grid-cols-2">
              <LiveNow data={data} />
              <QueueHealth data={data} />
            </div>

            <RecentAdminActivity data={data} />
          </>
        )}
      </div>
    </RequireCapability>
  );
}

function AttentionSummary({ counts }: { counts: DashboardPayload['counts'] }) {
  const total = counts.critical + counts.warning + counts.info;
  if (total === 0) return <span>Nothing needs attention</span>;

  return (
    <span className="flex flex-wrap items-center gap-x-2">
      {counts.critical > 0 && (
        <span style={{ color: 'var(--err-soft)' }}>{counts.critical} critical</span>
      )}
      {counts.warning > 0 && (
        <span style={{ color: 'var(--warn)' }}>{counts.warning} warning</span>
      )}
      {counts.info > 0 && <span className="text-tx3">{counts.info} info</span>}
    </span>
  );
}

/* --------------------------------------------------------- attention queue */

function AttentionQueue({ data }: { data: DashboardPayload }) {
  if (data.attention.length === 0) {
    return (
      <StateBlock
        icon={CheckCircle2}
        tone="neutral"
        title="Nothing needs attention"
        description="No league has a stuck week, a lifecycle error or an overdue lock, and no admin action is unsettled. This is the state you want."
      />
    );
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2
          className="font-head text-tx2 text-[11px] font-semibold uppercase"
          style={{ letterSpacing: '0.16em' }}
        >
          Attention queue
        </h2>
        {data.attentionTruncated && (
          <span className="text-tx3 text-[11.5px]">
            showing {data.attention.length} of {data.attentionTotal}
          </span>
        )}
      </div>

      <ul className="flex flex-col gap-2">
        {data.attention.map((item) => (
          <li key={item.id}>
            <TriageRow item={toTriageItem(item)} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function toTriageItem(item: AttentionItem): TriageItem {
  return {
    id: item.id,
    title: item.title,
    subject: item.subjectLabel,
    age: formatAge(item.since),
    severity: item.severity,
    state: item.state ?? undefined,
    to: item.to,
  };
}

/* ---------------------------------------------------------------- live now */

function LiveNow({ data }: { data: DashboardPayload }) {
  return (
    <Panel title="Live now" icon={Radio} count={data.live.length}>
      {data.live.length === 0 ? (
        <EmptyState
          title="No live weeks"
          description="No league is currently playing or being scored."
        />
      ) : (
        <ul className="flex flex-col gap-1.5">
          {data.live.map((league) => (
            <li
              key={league.id}
              className="border-bd/60 flex flex-wrap items-center justify-between gap-2 border-b pb-1.5 last:border-b-0 last:pb-0"
            >
              <Link
                to="/leagues/$leagueId"
                params={{ leagueId: league.id }}
                className="text-tx min-w-0 flex-1 truncate text-[13px] no-underline hover:underline"
              >
                {league.name}
              </Link>
              <div className="flex items-center gap-1.5">
                <span className="mesh-mono text-tx3 text-[11.5px]">
                  W{league.week === null ? '?' : league.week}
                </span>
                {league.status && <StateChip state={league.status} size="sm" />}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------ queue health */

function QueueHealth({ data }: { data: DashboardPayload }) {
  const style = QUEUE_STATUS_STYLE[data.queues.status];
  // Worst-first: a healthy queue in a list of sixteen is not what anyone opened
  // this panel to read, and an UNREADABLE queue must outrank a merely failing one.
  const ranked = [...data.queues.queues].sort(compareQueues);
  const notable = ranked.filter((queue) => queue.failed !== 0 || queue.depth > 0).slice(0, 6);

  return (
    <Panel
      title="Worker health"
      icon={Activity}
      actions={
        <Link
          to="/data-ops"
          className="text-[11px] no-underline hover:underline"
          style={{ color: style.text }}
        >
          {style.label}
        </Link>
      }
    >
      {data.queues.unreachable ? (
        <StateBlock
          icon={AlertTriangle}
          tone="danger"
          title="Queue health unreachable"
          description="Redis could not be read, so worker state is unknown. This is reported as backed up rather than healthy on purpose — a failed check is not a pass."
        />
      ) : notable.length === 0 ? (
        <p className="text-tx3 text-[12.5px]">
          All {data.queues.queues.length} queues idle with no failures.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {notable.map((queue) => (
            <li key={queue.name} className="flex items-center justify-between gap-2">
              <span className="mesh-mono text-tx2 truncate text-[12px]">{queue.name}</span>
              <span className="flex items-center gap-2.5">
                {queue.depth > 0 && (
                  <span className="mesh-mono text-tx3 text-[11.5px]">
                    {queue.depth.toLocaleString()} queued
                  </span>
                )}
                {/* -1 is the sentinel for "could not read this queue". */}
                {queue.failed !== 0 && (
                  <span
                    className="mesh-mono text-[11.5px]"
                    style={{ color: 'var(--err-soft)' }}
                  >
                    {queue.failed < 0 ? 'unreadable' : `${String(queue.failed)} failed`}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* --------------------------------------------------------- recent activity */

function RecentAdminActivity({ data }: { data: DashboardPayload }) {
  return (
    <Panel
      title="Recent admin activity"
      icon={ScrollText}
      actions={
        <Link to="/audit" className="text-tx3 text-[11px] no-underline hover:underline">
          Full audit log
        </Link>
      }
    >
      {data.recentActivity.length === 0 ? (
        <EmptyState
          title="No admin actions yet"
          description="Every Tier 2 and Tier 3 action lands here as it happens."
        />
      ) : (
        <ul className="flex flex-col gap-1.5">
          {data.recentActivity.map((row) => (
            <li
              key={row.id}
              className="border-bd/60 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b pb-1.5 last:border-b-0 last:pb-0"
            >
              <div className="min-w-0 flex-1">
                <span className="mesh-mono text-tx text-[12px]">{row.action}</span>
                <span className="text-tx3 ml-2 text-[11.5px]">
                  {row.entityLabel} · {row.actorName}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <TierDot tier={row.dangerTier} />
                <StateChip
                  state={row.outcome}
                  size="sm"
                  tone={
                    row.outcome === 'succeeded'
                      ? 'good'
                      : row.outcome === 'pending'
                        ? 'warn'
                        : 'bad'
                  }
                />
                <span className="mesh-mono text-tx3 text-[11px]">
                  {row.createdAt ? formatAge(row.createdAt) : '—'}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** Compact tier marker. Colour matches the danger-tier system used everywhere else. */
export function TierDot({ tier }: { tier: number }) {
  const color = tier === 3 ? 'var(--err)' : tier === 2 ? 'var(--warn)' : 'var(--tx3)';
  return (
    <span
      title={`Tier ${String(tier)}`}
      aria-label={`Danger tier ${String(tier)}`}
      className="mesh-mono text-[10px] font-semibold"
      style={{ color }}
    >
      T{tier}
    </span>
  );
}

/* ------------------------------------------------------------------- shell */

function Panel({
  title,
  icon: Icon,
  count,
  actions,
  children,
}: {
  title: string;
  icon?: typeof Activity;
  count?: number;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="border-bd bg-card rounded-xl border p-3.5">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <h2
          className="font-head text-tx2 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
          style={{ letterSpacing: '0.16em' }}
        >
          {Icon && <Icon size={12} aria-hidden />}
          {title}
          {count !== undefined && (
            <span className="mesh-mono text-tx3 text-[11px]">{count}</span>
          )}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}
